"use client";

import type { CSSProperties } from "react";
import { cn } from "@to/ui";
import { Icon } from "./icon";

import { pageWindow } from "@/lib/page-window";

/**
 * Profile-page v1 re-skin — presentational client-state pagination shared by
 * the Listings and Reviews sections. Mirrors the Find Your Agent results
 * pagination idiom (`agent-results.tsx`: prev/next icon buttons around a
 * `pageWindow` of numbered buttons), but driven by local `useState` in the
 * parent section instead of URL params, so prev/next here is a slim pair
 * (no first/last jump buttons).
 *
 * Returns null when there is nothing to paginate (`totalPages <= 1`).
 * All colors via `--brand-*`; icons via `Icon`.
 */
export function ProfilePagination({
  page,
  totalPages,
  onPageChange,
  ariaLabel = "Pagination",
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  ariaLabel?: string;
}) {
  if (totalPages <= 1) return null;

  return (
    <nav
      className="mt-6 flex items-center justify-center gap-1.5"
      aria-label={ariaLabel}
    >
      <ArrowButton
        label="Previous page"
        icon="chevron_left"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
      />
      {pageWindow(page, totalPages).map((p, idx) =>
        p === "ellipsis" ? (
          <span
            key={`e${idx}`}
            className="inline-flex h-9 min-w-9 items-center justify-center text-[13.5px]"
            style={{ color: "var(--brand-text-muted)" }}
            aria-hidden="true"
          >
            {"…"}
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-label={`Page ${p}`}
            aria-current={page === p ? "page" : undefined}
            className={cn(
              "inline-flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-[13.5px]",
              page === p ? "font-semibold" : "ring-1",
            )}
            style={
              page === p
                ? {
                    backgroundColor: "var(--brand-primary)",
                    color: "var(--brand-primary-foreground)",
                  }
                : ({
                    backgroundColor: "var(--brand-card-bg)",
                    color: "var(--brand-text)",
                    "--tw-ring-color": "var(--brand-card-border)",
                  } as CSSProperties)
            }
          >
            {p}
          </button>
        ),
      )}
      <ArrowButton
        label="Next page"
        icon="chevron_right"
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
      />
    </nav>
  );
}

function ArrowButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border disabled:opacity-30"
      style={{
        borderColor: "var(--brand-card-border)",
        color: "var(--brand-text-muted)",
        backgroundColor: "var(--brand-card-bg)",
      }}
    >
      <Icon name={icon} size={18} />
    </button>
  );
}
